import { Controller } from "@hotwired/stimulus"

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default class extends Controller {
  static targets = [
    "dispatchData",
    "availableList",
    "assignedList",
    "summary",
    "searchInput",
    "notifyCheckbox",
    "confirmCheckbox",
    "assetTab",
    "serviceSelect"
  ]

  static values = {
    accountUsers: Array,
    vehicles: Array,
    equipment: Array,
    orderData: Object
  }

  constructor(...args) {
    super(...args)
    this.dispatchState = {
      services: {},
      availableAssets: {
        labor: [],
        vehicles: [],
        equipment: []
      }
    }
    this.currentServiceId = null
    this.currentAssetType = 'labor'
  }

  connect() {
    //console.log("MoverDispatchController connected")
    this.initializeDispatchState()
    this.renderServiceSelector()
    this.renderAssets()
  

    this.updateVehicleDriver = this.updateVehicleDriver.bind(this)
    this.debouncedHandleEstimateUpdate = debounce(this.handleEstimateUpdate.bind(this), 420)
    document.addEventListener('estimateDataUpdated', this.debouncedHandleEstimateUpdate)
  }
  
  disconnect() {
    document.removeEventListener('estimateDataUpdated', this.debouncedHandleEstimateUpdate)
  }

  initializeDispatchState() {
    let dispatchDataValue = this.dispatchDataTarget.value
    let parsedData = {}

    if (dispatchDataValue && dispatchDataValue !== "null" && dispatchDataValue !== "undefined") {
      try {
        parsedData = JSON.parse(dispatchDataValue)
      } catch (e) {
        console.error("Error parsing dispatch data:", e)
      }
    }

    this.dispatchState = this.prepareDispatchState(parsedData)
  }

  prepareDispatchState(data = {}) {
    // Here we'll implement the logic to prepare the dispatch state
    // We'll need to process accountUsers, vehicles, and equipment
    // and only keep necessary fields

    return {
      services: data.services || {},
      availableAssets: {
        labor: this.processLabor(this.accountUsersValue),
        vehicles: this.processVehicles(this.vehiclesValue),
        equipment: this.processEquipment(this.equipmentValue)
      }
    }
  }

  processLabor(laborData) {
    // Process and return only necessary fields for labor
    return laborData.map(user => ({
      id: user.id,
      name: `${user.first_name} ${user.last_name}`,
      profilePictureUrl: user.cached_profile_picture_url
    }))
  }

  processVehicles(vehicleData) {
    // Process and return only necessary fields for vehicles
    return vehicleData.map(vehicle => ({
      id: vehicle.id,
      name: vehicle.general_info?.name || 'Unnamed Vehicle',
      make: vehicle.general_info?.make,
      model: vehicle.general_info?.model,
      year: vehicle.general_info?.year,
      licensePlate: vehicle.general_info?.license_plate,
      profilePictureUrl: vehicle.cached_profile_picture_url
    }))
  }

  processEquipment(equipmentData) {
    // Process and return only necessary fields for equipment
    return equipmentData.map(equipment => ({
      id: equipment.id,
      name: equipment.name,
      profilePictureUrl: equipment.cached_profile_picture_url
    }))
  }

  enderServiceSelector() {
    const selector = this.serviceSelectTarget;
    selector.innerHTML = ''; // Clear existing options
    
    if (!this.dispatchState || !this.dispatchState.services) {
      console.error('Dispatch state or services are undefined');
      return;
    }
  
    Object.entries(this.dispatchState.services).forEach(([id, service]) => {
      try {
        const optionText = this.formatServiceOption(service);
        const option = new Option(optionText, id);
        selector.add(option);
      } catch (error) {
        console.error('Error creating option for service:', id, error);
      }
    });
    
    selector.addEventListener('change', this.handleServiceChange.bind(this));
    
    // Select the first service by default
    if (selector.options.length > 0) {
      this.currentServiceId = selector.options[0].value;
      selector.value = this.currentServiceId;
    } else {
      console.warn('No services available to select');
    }
  }

  formatServiceOption(service) {
    if (!service || typeof service !== 'object') {
      return 'Invalid Service';
    }
  
    let optionText = 'Unnamed Service';
  
    if (service.details && typeof service.details === 'object') {
      optionText = service.details.name || 'Unnamed Service';
  
      if (service.details.address && typeof service.details.address === 'object') {
        const address = service.details.address;
        const addressParts = [
          address.address,
          address.apartment,
          address.city,
          address.state,
          address.postalCode
        ].filter(Boolean); // Remove empty parts
        
        if (addressParts.length > 0) {
          optionText += ` - ${addressParts.join(', ')}`;
        }
      }
    } else if (service.name) {
      optionText = service.name;
    }
  
    return optionText;
  }

  handleServiceChange(event) {
    this.currentServiceId = event.target.value
    this.renderAssets()
  }

  renderAssets() {
    this.renderAvailableAssets()
    this.renderAssignedAssets()
    this.updateSummary()
  }

  renderAvailableAssets() {
    const container = this.availableListTarget
    container.innerHTML = `<h6 class="font-semibold mb-4">Available ${this.capitalizeFirstLetter(this.currentAssetType)}</h6>`
    const gridContainer = document.createElement('div')
    gridContainer.className = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
    
    this.dispatchState.availableAssets[this.currentAssetType].forEach(asset => {
      if (!this.isAssetAssignedToCurrentService(asset)) {
        const element = this.createAssetElement(asset, false)
        gridContainer.appendChild(element)
      }
    })
    
    container.appendChild(gridContainer)
  }


  renderAssignedAssets() {
    //console.log("rendersAsssigneAssets")
    const service = this.dispatchState.services[this.currentServiceId];
    if (!service) {
      console.warn('No service found for current service ID:', this.currentServiceId);
      return;
    }
  
    this.assignedListTarget.innerHTML = `<h6 class="font-semibold mb-4">Assigned ${this.capitalizeFirstLetter(this.currentAssetType)}</h6>`;
    const gridContainer = document.createElement('div');
    gridContainer.className = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4';
    
    if (service[this.currentAssetType]) {
      //console.log(`Rendering assigned ${this.currentAssetType}:`, service[this.currentAssetType]);
      service[this.currentAssetType].forEach(asset => {
        const element = this.createAssetElement(asset, true);
        gridContainer.appendChild(element);
      });
    } else {
      console.warn(`No assigned ${this.currentAssetType} for current service`);
    }
    
    this.assignedListTarget.appendChild(gridContainer);
  }

  createAssetElement(asset, isAssigned) {
    //console.log("craeteAssetElement")
    const templateId = `${isAssigned ? 'assigned' : 'available'}-${this.currentAssetType}-template`
    const template = document.getElementById(templateId)
    const element = template.content.cloneNode(true).firstElementChild
    
    element.dataset.assetId = asset.id
    
    const nameElement = element.querySelector('[data-asset-name]')
    if (nameElement) nameElement.textContent = asset.name

    this.setAssetImage(element, asset)

    if (this.currentAssetType === 'vehicles') {
      this.populateVehicleData(element, asset, isAssigned)
    } else if (this.currentAssetType === 'equipment' && isAssigned) {
      const quantityInput = element.querySelector('[data-field="quantity"]')
      if (quantityInput) {
        quantityInput.value = asset.quantity || 1
        quantityInput.dataset.assetId = asset.id  // Ensure this line exists
        //console.log("Created quantity input for asset:", asset.id)
      }
    }

    const actionButton = element.querySelector(isAssigned ? '[data-action="click->mover-dispatch#unassignAsset"]' : '[data-action="click->mover-dispatch#assignAsset"]')
    if (actionButton) actionButton.dataset.assetId = asset.id
    
    return element
  }

  setAssetImage(element, asset) {
    const imageContainer = element.querySelector('[data-asset-image]')
    if (imageContainer) {
      if (asset.profilePictureUrl) {
        imageContainer.innerHTML = `<img src="${asset.profilePictureUrl}" alt="${asset.name}" class="w-full h-full object-cover">`
      } else {
        this.setDefaultIcon(imageContainer)
      }
    }
  }

  setDefaultIcon(container) {
    let iconClass = 'fa-user'
    if (this.currentAssetType === 'vehicles') iconClass = 'fa-truck'
    if (this.currentAssetType === 'equipment') iconClass = 'fa-box'
    container.innerHTML = `<i class="fas ${iconClass} fa-lg"></i>`  
  } 


  populateVehicleData(element, vehicle, isAssigned) {

    //console.log("pouilating vehicle data")
    const makeModelYear = element.querySelector('[data-vehicle-make-model-year]');
    if (makeModelYear) {
      makeModelYear.textContent = `${vehicle.make || ''} ${vehicle.model || ''} (${vehicle.year || ''})`;
    }
  
    const licensePlate = element.querySelector('[data-vehicle-license-plate]');
    if (licensePlate) {
      licensePlate.textContent = vehicle.licensePlate || 'N/A';
    }
  
    const driverSelect = element.querySelector('select[data-vehicle-id]');
    if (driverSelect) {
      driverSelect.dataset.vehicleId = vehicle.id;
      if (isAssigned) {
        this.populateDriverSelect(driverSelect);
        driverSelect.value = vehicle.assigned_driver || '';
        driverSelect.classList.remove('hidden');
      } else {
        driverSelect.classList.add('hidden');
        driverSelect.value = '';
      }
    }
  }

  populateDriverSelect(select) {
    //console.log("populatingdriverss")
    select.innerHTML = '<option value="">Select a driver</option>';
    const service = this.dispatchState.services[this.currentServiceId];

 

    if (service && service.labor && Array.isArray(service.labor)) {
      //console.log('trying to load drivers for select')
      service.labor.forEach(laborer => {
        //console.log("laborder", laborer)
        const option = document.createElement('option');
        option.value = laborer.id;
        option.textContent = this.getAssetName(laborer, 'labor');
        select.appendChild(option);
      });
    } else {
      console.warn('No laborers available for driver selection');
    }
  }

  updateSummary() {
    const service = this.dispatchState.services[this.currentServiceId]
    if (!service) return

    const totalAssigned = service[this.currentAssetType] ? service[this.currentAssetType].length : 0
    const totalCost = this.calculateTotalCost(service)
    
    this.summaryTarget.innerHTML = `
      Total Assigned ${this.capitalizeFirstLetter(this.currentAssetType)}: ${totalAssigned} | 
      Estimated Cost: $${totalCost.toFixed(2)}
    `
  }

  calculateTotalCost(service) {
    // Implement cost calculation logic based on asset type and quantity
    // This is a placeholder implementation
    const laborCost = (service.labor || []).length * 100
    const vehicleCost = (service.vehicles || []).length * 200
    const equipmentCost = (service.equipment || []).reduce((sum, equipment) => sum + (equipment.quantity || 1) * 50, 0)
    return laborCost + vehicleCost + equipmentCost
  }

  isAssetAssignedToCurrentService(asset) {
    if (!this.currentServiceId) return false
    const service = this.dispatchState.services[this.currentServiceId]
    return service && service[this.currentAssetType] &&
           service[this.currentAssetType].some(a => a.id.toString() === asset.id.toString())
  }

  capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  getAssetName(asset, assetType) {
    switch (assetType) {
      case 'labor':
        return `${asset.name || ''}`.trim() || 'Unnamed Laborer';
      case 'vehicles':
        return asset.name || `${asset.make || ''} ${asset.model || ''} ${asset.year || ''}`.trim() || 'Unnamed Vehicle';
      case 'equipment':
        return asset.name || 'Unnamed Equipment';
      default:
        return 'Unnamed Asset';
    }
  }

  assignAsset(event) {
    const assetId = event.currentTarget.dataset.assetId;
    const asset = this.dispatchState.availableAssets[this.currentAssetType].find(a => a.id.toString() === assetId);
    if (asset && this.currentServiceId) {
      const service = this.dispatchState.services[this.currentServiceId];
      if (service) {
        if (!service[this.currentAssetType]) {
          service[this.currentAssetType] = [];
        }
        if (!service[this.currentAssetType].some(a => a.id.toString() === assetId)) {
          const assignedAsset = { ...asset };
          if (this.currentAssetType === 'equipment') {
            assignedAsset.quantity = 1;
          } else if (this.currentAssetType === 'vehicles') {
            assignedAsset.assigned_driver = null;
          }
          service[this.currentAssetType].push(assignedAsset);
          this.saveDispatchStateInput();
          this.renderAssets();
        }
      }
    }
  }
  
  unassignAsset(event) {
    const assetId = event.currentTarget.dataset.assetId
    if (this.currentServiceId) {
      const service = this.dispatchState.services[this.currentServiceId]
      if (service) {
        service[this.currentAssetType] = service[this.currentAssetType].filter(a => a.id.toString() !== assetId)
        
        if (this.currentAssetType === 'vehicles') {
          // Remove this vehicle's driver assignment
          service.labor = service.labor.map(laborer => {
            if (laborer.assigned_vehicle === assetId) {
              delete laborer.assigned_vehicle
            }
            return laborer
          })
        }
        
        this.saveDispatchStateInput()
        this.renderAssets()
      }
    }
  }

  updateAssetQuantity(event) {
    const assetId = event.target.dataset.assetId
    const quantity = parseInt(event.target.value, 10)
    
    // Find the current service
    const service = this.dispatchState.services[this.currentServiceId]
    
    if (service && service.equipment) {
      // Find the equipment item and update its quantity
      const equipmentItem = service.equipment.find(item => item.id.toString() === assetId)
      if (equipmentItem) {
        equipmentItem.quantity = quantity
        
        // Save the updated state
        this.saveDispatchStateInput()
        
        // Update the summary without re-rendering all assets
        this.updateSummary()
        
        // Update any other elements that display the quantity (if any)
        //this.updateQuantityDisplay(assetId, quantity)
      }
    }
  }

  updateVehicleDriver(event) {
    const vehicleId = event.target.dataset.vehicleId
    const driverId = event.target.value
    const service = this.dispatchState.services[this.currentServiceId]
    const vehicleIndex = service.vehicles.findIndex(v => v.id.toString() === vehicleId.toString())
    
    if (vehicleIndex !== -1) {
      service.vehicles[vehicleIndex].assigned_driver = driverId
      this.saveDispatchStateInput()
    }
  }

  saveDispatchStateInput() {
    const dataToSave = {
      services: this.dispatchState.services
    }
    this.dispatchDataTarget.value = JSON.stringify(dataToSave)
    //console.log("Saved dispatch state:", dataToSave) // For debugging
  }

  handleEstimateUpdate(event) {
    //console.log("Estimate data update received");
    const newEstimateData = event.detail;
    
    if (this.hasSignificantChanges(newEstimateData)) {
      //console.log("Significant changes detected, updating dispatch state");
      //console.log("New estimate data:", newEstimateData);
      this.updateDispatchStateFromEstimate(newEstimateData);
      this.renderServiceSelector();
      this.renderAssets();
    } else {
      //console.log("No significant changes, skipping update");
      //console.log("Current estimate data:", this.lastEstimateData);
      //console.log("New estimate data:", newEstimateData);
    }
  }

  hasSignificantChanges(newEstimateData) {
    if (!this.lastEstimateData) {
      this.lastEstimateData = newEstimateData;
      return true;
    }
  
    const oldServices = this.lastEstimateData.services || [];
    const newServices = newEstimateData.services || [];
  
    // Check if number of services has changed
    if (oldServices.length !== newServices.length) {
      this.lastEstimateData = newEstimateData;
      return true;
    }
  
    // Check if any service's details have changed
    for (let i = 0; i < newServices.length; i++) {
      const oldService = oldServices[i];
      const newService = newServices[i];
  
      if (this.haveServiceDetailsChanged(oldService, newService)) {
        this.lastEstimateData = newEstimateData;
        return true;
      }
    }
  
    return false;
  }
  
  haveServiceDetailsChanged(oldService, newService) {
    // Check if IDs are different
    if (oldService.id !== newService.id) {
      return true;
    }
  
    // Deep compare the details objects
    return JSON.stringify(oldService.details) !== JSON.stringify(newService.details);
  }
  
  updateDispatchStateFromEstimate(estimateData) {
    if (!estimateData || !estimateData.services || !Array.isArray(estimateData.services)) {
      console.error('Invalid estimate data structure')
      return
    }
  
    const newServices = {}
    
    estimateData.services.forEach(service => {
      if (service && typeof service === 'object') {
        const serviceId = service.id || `service-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        newServices[serviceId] = {
          details: service.details || {},
          labor: [],
          vehicles: [],
          equipment: []
        }
      }
    })
  
    // Preserve existing assignments if the service still exists
    Object.entries(this.dispatchState.services || {}).forEach(([id, existingService]) => {
      if (newServices[id]) {
        newServices[id].labor = existingService.labor || []
        newServices[id].vehicles = existingService.vehicles || []
        newServices[id].equipment = existingService.equipment || []
      }
    })
  
    this.dispatchState.services = newServices
    this.saveDispatchStateInput()
  }


  renderServiceSelector() {
    const selector = this.serviceSelectTarget;
    selector.innerHTML = ''; // Clear existing options
    
    if (!this.dispatchState || !this.dispatchState.services) {
      console.error('Dispatch state or services are undefined');
      return;
    }
  
    Object.entries(this.dispatchState.services).forEach(([id, service]) => {
      try {
        const optionText = this.formatServiceOption(service);
        const option = new Option(optionText, id);
        selector.add(option);
      } catch (error) {
        console.error('Error creating option for service:', id, error);
      }
    });
    
    selector.addEventListener('change', this.handleServiceChange.bind(this));
    
    // Select the first service by default
    if (selector.options.length > 0) {
      this.currentServiceId = selector.options[0].value;
      selector.value = this.currentServiceId;
    } else {
      console.warn('No services available to select');
    }
  }

  switchAssetType(event) {
    const clickedTab = event.currentTarget
    const assetType = clickedTab.dataset.assetType
  
    this.assetTabTargets.forEach(tab => {
      tab.classList.remove('bg-gray-200')
      tab.classList.add('bg-white', 'hover:bg-gray-100')
    })
    clickedTab.classList.remove('bg-white', 'hover:bg-gray-100')
    clickedTab.classList.add('bg-gray-200')
  
    this.currentAssetType = assetType
    this.renderAssets()
  }

  switchTab(event) {
    const clickedTab = event.currentTarget
    const assetType = clickedTab.dataset.assetType
  
    this.assetTabTargets.forEach(tab => {
      tab.classList.remove('bg-gray-200')
      tab.classList.add('bg-white')
    })
    clickedTab.classList.remove('bg-white')
    clickedTab.classList.add('bg-gray-200')
  
    this.currentAssetType = assetType
    this.renderAssets()
  }
}